import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  SweepDefaults,
  useSweepDefaults,
  useUpdateSweepDefaults,
} from '../../api/useSystemConfig';

interface Props {
  open: boolean;
  onClose: () => void;
}

/** A single numeric sweep param: its global field key, label, step and default. */
interface SweepField {
  key: keyof SweepDefaults;
  label: string;
  step: number;
  /** Canonical engine fallback shown when the global value is null/blank. */
  engineDefault: number;
}

/**
 * The KEY sweep params, surfaced at the top of the dialog. These are the levers
 * the operator tunes most often.
 */
const KEY_FIELDS: ReadonlyArray<SweepField> = [
  { key: 'sweepMinTicks', label: 'Min ticks', step: 1, engineDefault: 2 },
  {
    key: 'sweepKalshiOffsetLevels',
    label: 'Kalshi offset levels',
    step: 1,
    engineDefault: 1,
  },
  {
    key: 'sweepPolymarketOffsetLevels',
    label: 'Polymarket offset levels',
    step: 1,
    engineDefault: 1,
  },
  {
    key: 'adverseCancelTicks',
    label: 'Adverse cancel ticks',
    step: 1,
    engineDefault: 2,
  },
];

/** The rest of the params, tucked behind the collapsible "Avanzado" section. */
const ADVANCED_FIELDS: ReadonlyArray<SweepField> = [
  { key: 'sweepWindowMs', label: 'Window (ms)', step: 50, engineDefault: 400 },
  {
    key: 'sweepMinLevelsCleared',
    label: 'Min levels cleared',
    step: 1,
    engineDefault: 2,
  },
  {
    key: 'sweepDepthFraction',
    label: 'Depth fraction',
    step: 0.1,
    engineDefault: 0.5,
  },
  {
    key: 'sweepMinContractsFloor',
    label: 'Min contracts floor',
    step: 1,
    engineDefault: 10,
  },
  { key: 'sweepDeltaSigma', label: 'Delta sigma', step: 0.5, engineDefault: 3.0 },
  { key: 'sweepConfirmMs', label: 'Confirm (ms)', step: 50, engineDefault: 0 },
  { key: 'sweepFreshMs', label: 'Fresh (ms)', step: 50, engineDefault: 500 },
  { key: 'sweepResolveMs', label: 'Resolve (ms)', step: 50, engineDefault: 750 },
  { key: 'sweepRefireMinTicks', label: 'Refire min ticks', step: 1, engineDefault: 1 },
  {
    key: 'liveMakerSizeFactor',
    label: 'Live maker size factor',
    step: 0.1,
    engineDefault: 1.0,
  },
];

const ALL_FIELDS: ReadonlyArray<SweepField> = [...KEY_FIELDS, ...ADVANCED_FIELDS];

/**
 * Global-only editor for the sweep-response defaults shared by every rule.
 *
 * Each field is prefilled from the global `sweepDefaults`; when a global field
 * is null it shows the canonical engine default so the operator always sees a
 * concrete value (never blank). Save sends all 14 fields through
 * `useUpdateSweepDefaults`, making the global config concrete.
 *
 * The KEY params (Min ticks, Kalshi/Polymarket offset levels, Adverse cancel
 * ticks) are shown up front; the rest live in a collapsed "Avanzado" section.
 *
 * Per-rule on/off lives in the table's Sweep checkbox column, NOT here.
 */
export const SweepDualPostEditorDialog = ({ open, onClose }: Props) => {
  const { data: sweepDefaults } = useSweepDefaults();
  const updateGlobalMutation = useUpdateSweepDefaults();

  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Per-field string values, keyed by field key.
  const [values, setValues] = useState<Record<string, string>>({});

  const globalValueOf = (key: keyof SweepDefaults): number | null | undefined =>
    sweepDefaults ? sweepDefaults[key] : undefined;

  // Initialise the form whenever it opens or the global data changes. A null /
  // missing global field falls back to the engine default so the operator
  // always sees a real value.
  useEffect(() => {
    if (!open) return;
    setAdvancedOpen(false);
    const next: Record<string, string> = {};
    for (const f of ALL_FIELDS) {
      const g = globalValueOf(f.key);
      next[f.key] =
        g === undefined || g === null ? String(f.engineDefault) : String(g);
    }
    setValues(next);
  }, [open, sweepDefaults]);

  const setField = (key: string, v: string) =>
    setValues(prev => ({ ...prev, [key]: v }));

  const parseField = (v: string): number | null =>
    v.trim() === '' ? null : Number(v);

  const handleSave = async () => {
    const dto: Partial<SweepDefaults> = {};
    for (const f of ALL_FIELDS) {
      dto[f.key] = parseField(values[f.key] ?? '');
    }
    await updateGlobalMutation.mutateAsync(dto);
    onClose();
  };

  const isSaving = updateGlobalMutation.isLoading;

  const numField = (f: SweepField) => (
    <TextField
      label={f.label}
      type="number"
      size="small"
      value={values[f.key] ?? ''}
      placeholder={String(f.engineDefault)}
      onChange={e => setField(f.key, e.target.value)}
      inputProps={{ step: f.step, min: 0 }}
      InputLabelProps={{ shrink: true }}
      fullWidth
    />
  );

  const fieldRows = (fields: ReadonlyArray<SweepField>) => (
    <Stack spacing={1.5}>
      {Array.from({ length: Math.ceil(fields.length / 2) }).map((_, row) => {
        const a = fields[row * 2];
        const b = fields[row * 2 + 1];
        return (
          <Stack direction="row" spacing={2} key={a.key}>
            {numField(a)}
            {b ? (
              numField(b)
            ) : (
              <TextField sx={{ visibility: 'hidden' }} size="small" fullWidth />
            )}
          </Stack>
        );
      })}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontSize: '1rem' }}>
        Configuración global de Sweep response
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography sx={{ fontSize: '0.75rem', color: 'grey.500' }}>
            Estos valores aplican a todas las reglas con Sweep activado.
          </Typography>

          {fieldRows(KEY_FIELDS)}

          <Box>
            <Button
              size="small"
              onClick={() => setAdvancedOpen(o => !o)}
              startIcon={
                advancedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
              }
              sx={{ textTransform: 'none', color: 'grey.400' }}
            >
              Avanzado
            </Button>
            <Collapse in={advancedOpen} unmountOnExit>
              <Box sx={{ mt: 1.5 }}>{fieldRows(ADVANCED_FIELDS)}</Box>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          size="small"
        >
          {isSaving ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
